import {Button, Input, Select, SelectItem, Textarea} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {createFileRoute} from "@tanstack/react-router";
import React, {useState} from "react";
import {apiAction} from "../api.ts";
import {config} from "../config/config.ts";
import {SportTypes} from "../constants.ts";
import FileUploader from "../components/FileUploader.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/list-tournament')({
    component: RouteComponent,
})

// "Placement": list a tournament that is run elsewhere (not on ScorePal) in our public directory.
// Submissions reuse the public `contact` endpoint (filed as a ticket the team reviews) — no new
// backend needed for the stub.
function RouteComponent() {
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        tournament_name: '', sport: '', starts_at: '', ends_at: '', location: '', url: '', description: '', image_url: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k: string, v: string) => setForm((f) => ({...f, [k]: v}));

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim() || !form.tournament_name.trim()) {
            addToast({title: 'Please add your name, email and the tournament name.', color: 'danger'});
            return;
        }
        setSubmitting(true);
        try {
            const res = await apiAction('tournament-placements-create' as any, {
                title:         form.tournament_name,
                sport_type:    form.sport,
                starts_at:     form.starts_at,
                ends_at:       form.ends_at,
                location:      form.location,
                url:           form.url,
                description:   form.description,
                contact_name:  form.name,
                contact_email: form.email,
                contact_phone: form.phone,
                image_url:     form.image_url,
            });
            if (res?.error) {
                addToast({title: res.error, color: 'danger'});
                setSubmitting(false);
                return;
            }
            setDone(true);
        } catch (e) {
            addToast({title: 'Something went wrong. Please try again.', color: 'danger'});
        }
        setSubmitting(false);
    };

    return (
        <main className="min-h-screen bg-[#EDEEF4] font-inter text-[#071a5f]">
            <title>List Your Tournament | ScorePal</title>
            <meta name="description" content="List an external tournament in the ScorePal directory so players can discover it." />
            <link rel="canonical" href={`${config.publicUrl}/list-tournament/`} />

            {/* Hero — compact, matches /find-tournaments/ */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20"><LandingNav /></div>
                    <div className="container mx-auto px-6 pt-8 lg:px-12">
                        <div className="max-w-3xl">
                            <h1 className="mb-3 font-bebas text-[clamp(36px,5vw,60px)] font-bold uppercase leading-none tracking-wide text-white">
                                List your tournament
                            </h1>
                            <p className="max-w-2xl font-inter text-base leading-relaxed text-white/85 lg:text-lg">
                                Running a tournament elsewhere? Submit the details and we'll add it to the ScorePal
                                directory so players can find it. Want full registration, draws and live scoring instead?{' '}
                                <a className="font-semibold underline" href="/signup/organizer/">Run it on ScorePal</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="container mx-auto max-w-2xl px-4 pb-20 lg:px-12">
                <div className="-mt-6 rounded-3xl border border-[#dfe6f5] bg-white p-6 shadow-[0_18px_45px_rgba(8,47,138,0.12)] sm:p-8">
                    {done ? (
                        <div className="py-10 text-center">
                            <h2 className="font-bebas text-3xl text-[#06195f]">Thanks — we've got it!</h2>
                            <p className="mt-2 text-[#5770ab]">
                                Your tournament has been submitted for listing. We'll review it and add it to the directory shortly.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            <h2 className="font-bebas text-2xl text-[#06195f]">Tournament details</h2>
                            <Input isRequired label="Tournament name" value={form.tournament_name} onValueChange={(v) => set('tournament_name', v)} />
                            <Select label="Sport" selectedKeys={form.sport ? [form.sport] : []} onChange={(e) => set('sport', e.target.value)}>
                                {Object.entries(SportTypes).map(([k, v]) => <SelectItem key={k}>{v as string}</SelectItem>)}
                            </Select>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Input type="datetime-local" label="Start date & time" value={form.starts_at} onValueChange={(v) => set('starts_at', v)} />
                                <Input type="datetime-local" label="End date & time (optional)" value={form.ends_at} onValueChange={(v) => set('ends_at', v)} />
                            </div>
                            <Input label="Location / venue" value={form.location} onValueChange={(v) => set('location', v)} />
                            <Input label="Registration link / website" type="url" placeholder="https://" value={form.url} onValueChange={(v) => set('url', v)} />
                            <Textarea label="Details" minRows={3} value={form.description} onValueChange={(v) => set('description', v)} />

                            <div>
                                <label className="mb-1 block text-sm text-[#5770ab]">Tournament image (optional)</label>
                                <div className="flex items-center gap-3">
                                    {form.image_url && <img src={form.image_url} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
                                    <FileUploader
                                        className="flex-1"
                                        category="posts"
                                        buttonText={form.image_url ? 'Change image' : 'Upload image'}
                                        onUploaded={(data) => set('image_url', data.image_url)}
                                    />
                                </div>
                            </div>

                            <h2 className="mt-2 font-bebas text-2xl text-[#06195f]">Your contact</h2>
                            <Input isRequired label="Your name" value={form.name} onValueChange={(v) => set('name', v)} />
                            <Input isRequired label="Email" type="email" value={form.email} onValueChange={(v) => set('email', v)} />
                            <Input label="Phone (optional)" value={form.phone} onValueChange={(v) => set('phone', v)} />

                            <Button color="primary" size="lg" isLoading={submitting} onPress={submit} className="mt-2">
                                Submit listing
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <FooterSection />
        </main>
    );
}
